import React, {useMemo} from "react";
import {Steps} from "antd";
import {useTranslation} from "react-i18next";
import StepHeader from "./StepHeader";
import {useStore} from "../../../../hooks/useStore";
import StepDescription from "./StepDescription";
import {Container, TrialStep, TrialStepContainer} from "./OrderStepsStyles";
import CustomDot from "./CustomDot";

const OrderSteps = ({orderData, transferData, renderProofIcon}) => {
    const stores = useStore();
    const {t} = useTranslation(["orderDetail"]);

    // delivery info
    const createdAt = orderData.data?.delivery.createdAt;
    const endDate = orderData.data?.steps?.slice(-1)[0]?.endDate;
    const currentDeliveryState = orderData?.data?.delivery?.deliveryState;
    const currentStepIndex = orderData.data.delivery.currentStepIndex;
    const steps = orderData?.data?.steps;
    const deliveryType = orderData?.data?.delivery?.deliveryType;
    const routes = orderData.data?.estimation?.routes;
    const summary = orderData.data?.delivery?.summary;

    const stepsData = useMemo(() => {
        if (["canceled", "rejected", "returned"].includes(currentDeliveryState)) {
            return steps?.slice(0, currentStepIndex);
        } else {
            return orderData?.data?.steps;
        }
    }, [currentStepIndex, currentDeliveryState, orderData?.data?.steps]);

    const currentStep = useMemo(
        () =>
            endDate && ["delivered", "returned"].includes(currentDeliveryState)
                ? currentStepIndex + 1
                : currentStepIndex - 1,
        [endDate, currentDeliveryState, currentStepIndex]
    );

    const getStepInfo = (step, index) => {
        const orderNumber = step.stepInfo.orderNumber;
        const activeLink = orderData.data.delivery.currentStepIndex <= index;
        const assignedCourierId = orderData.data.delivery.assignedCourier._id;

        const transferCouriers = transferData?.data
            ?.map(transferItem => [transferItem.fromCourier, transferItem.toCourier])
            .flat();

        const stepCourier = transferCouriers?.find(
            courier => (step.courierId || assignedCourierId) === courier._id
        );

        const assignedCourier =
            stepCourier?.fullname || orderData.data.delivery.assignedCourier.fullname;
        const courierLinkTo =
            stores.meStore.checkRole("courierDetail") && `/couriers/${assignedCourierId}`;
        const stepInfoName =
            step.stepInfo?.name || step.stepInfo?.receiverName || step.stepInfo?.receiptNumber;
        const isReceiptNumber = step.stepInfo?.receiptNumber && !step.stepInfo?.receiverName;

        const orders = orderData.data.delivery.orders || [];
        const allOrderInformations =
            orders.reduce((acc, order) => {
                if (order.orderInformations && order.orderInformations.length > 0) {
                    const notificationsWithOrderInfo = order.orderInformations.map(
                        notification => ({
                            ...notification,
                            orderReceiverName: order.receiverName || order.orderNumber,
                            orderId: order._id
                        })
                    );
                    acc.push(...notificationsWithOrderInfo);
                }
                return acc;
            }, []) || [];

        let stepOrderIds = [];
        let receiverName = null;
        // 1. Öncelik: stepInfo.orders
        if (step.stepInfo.orders && step.stepInfo.orders.length > 0) {
            stepOrderIds = step.stepInfo.orders.map(o => o._id);
            if (stepOrderIds.length === 1) {
                const order = orders.find(o => o._id === stepOrderIds[0]);
                receiverName = order?.receiverName || order?.orderNumber;
            }
        } else if (
            ![
                "onTheWayForPickUp",
                "onReturnAddress",
                "onTheWayForReturn",
                "onPickUpAddress",
                "waiting"
            ].includes(step.deliveryState) &&
            step.stepInfo._id
        ) {
            // 2. Öncelik: stepInfo._id = orderInformation.groupId (sadece delivery stepleri için)
            stepOrderIds = [step.stepInfo._id];
            const order = orders.find(o => o._id === step.stepInfo._id);
            receiverName = order?.receiverName || order?.orderNumber;
        } else if (
            step.deliveryState === "waiting" &&
            step.stepInfo.receivers &&
            step.stepInfo.receivers.length > 0
        ) {
            // 3. Öncelik: waiting state'inde receivers dizisi
            const receiverOrderNumbers = step.stepInfo.receivers.map(r => r.orderNumber);
            stepOrderIds = orders
                .filter(o => receiverOrderNumbers.includes(o.orderNumber))
                .map(o => o._id);
            if (step.stepInfo.receivers.length === 1) {
                receiverName = step.stepInfo.receivers[0].name;
            }
        }

        // Notification filtreleme
        let stepOrderInformations = [];
        if (stepOrderIds.length > 0) {
            // Sadece ilgili orderId'lere sahip ve gönderilmiş tüm notification'lar (state bakma)
            stepOrderInformations = allOrderInformations.filter(
                n => stepOrderIds.includes(n.groupId) && n.isSent
            );
        } else {
            // Hiçbir eşleşme yoksa sadece gönderilmiş tüm notification'lar
            stepOrderInformations = allOrderInformations.filter(n => n.isSent);
        }

        // Sipariş veya pickup index'ini bul
        let stepPrefix = "";
        let stepPrefixType = "";
        let orderState = null;
        if (
            step.deliveryState === "onPickUpAddress" ||
            step.deliveryState === "onTheWayForPickUp"
        ) {
            // Pickup step ise - stepInfo.index kullan
            if (step.stepInfo?.index !== undefined) {
                stepPrefix = `P${step.stepInfo.index}`;
                stepPrefixType = "pickup";
            }
        } else if (
            step.deliveryState === "onDeliveryAddress" ||
            step.deliveryState === "onDelivery"
        ) {
            // Order step ise - orders array'inden gerçek index'i al
            const order = orders.find(o => o._id === step.stepInfo._id);
            if (order?.index !== undefined) {
                stepPrefix = `O${order.index}`;
                stepPrefixType = "order";
                orderState = order?.state;
            }
        }

        return {
            state: step.deliveryState,
            stepInfoName,
            isReceiptNumber,
            cardType: step.cardType,
            _id: step.stepInfo?._id,
            stepIndex: step.stepIndex,
            mergedAt: step.stepInfo?.mergedAt,
            startDate: step.startDate,
            reorderDate: step.reorderDate,
            endDate: step.endDate,
            passed: step.stepInfo.passed,
            canceled: step?.stepInfo?.canceled,
            orderNumber,
            courierLinkTo,
            assignedCourier,
            activeLink,
            trial: step.trial,
            orderInformations: stepOrderInformations,
            receiverName,
            stepPrefix,
            stepPrefixType,
            orderState
        };
    };

    const renderProgressDot = (iconDot, {index, status}) => {
        if (index >= stepsData.length) {
            return iconDot;
        }
        const step = stepsData[index];
        const info = getStepInfo(step, index);
        return (
            <CustomDot
                prefix={info.stepPrefix || ""}
                prefixType={info.stepPrefixType || ""}
                orderState={info.orderState || ""}
                status={status}
            />
        );
    };

    return (
        <Container>
            <Steps progressDot={renderProgressDot} direction="vertical" current={currentStep}>
                {stepsData.map((step, index) => {
                    const info = getStepInfo(step, index);
                    return (
                        <React.Fragment key={index}>
                            <Steps.Step
                                data-testid="orderDetailCard_step"
                                data-testvalue={index}
                                data-prefix={info.stepPrefix || ""}
                                data-prefix-type={info.stepPrefixType || ""}
                                data-order-state={info.orderState || ""}
                                key={index}
                                title={
                                    <StepHeader
                                        {...info}
                                        routes={routes}
                                        deliveryType={deliveryType}
                                    />
                                }
                                description={
                                    <StepDescription
                                        {...info}
                                        currentStepIndex={currentStepIndex}
                                        deliveryCreatedAt={createdAt}
                                        renderProofIcon={() => renderProofIcon(step)}
                                    />
                                }
                            />
                            {info.state === "onDeliveryAddress" && Boolean(info.trial) && (
                                <TrialStepContainer
                                    id={"trial-step"}
                                    iscompleted={Boolean(info.trial.endDate).toString()}
                                >
                                    <Steps progressDot direction="vertical" current={2}>
                                        <TrialStep
                                            data-testid="orderDetailCard_step"
                                            data-testvalue={index}
                                            key={index}
                                            icon={<React.Fragment />}
                                            title={
                                                <StepHeader
                                                    {...info}
                                                    isTrial
                                                    routes={routes}
                                                    deliveryType={deliveryType}
                                                />
                                            }
                                            description={
                                                <StepDescription
                                                    {...info.trial}
                                                    startDate={info.trial.startDate}
                                                    endDate={info.trial.endDate}
                                                    currentStepIndex={currentStepIndex}
                                                    deliveryCreatedAt={createdAt}
                                                    renderProofIcon={() => renderProofIcon(step)}
                                                />
                                            }
                                        />
                                        <Steps.Step style={{display: "none"}} />
                                    </Steps>
                                </TrialStepContainer>
                            )}
                        </React.Fragment>
                    );
                })}
                <Steps.Step
                    data-testid="orderDetailCard_step"
                    data-testvalue={"lastStep"}
                    title={
                        summary && summary?.types?.length !== 0
                            ? summary.types.map(
                                  type =>
                                      t(`deliveryInfo.summary.${type}`, {
                                          ns: "orderDetail",
                                          ordersCount: summary.ordersCount,
                                          deliveredOrders: summary.deliveredOrders,
                                          canceledOrders: summary.canceledOrders,
                                          canceledOrdersBeforePickup:
                                              summary.canceledOrdersBeforePickup,
                                          productsCount: summary.productsCount,
                                          trialProducts: summary.trialProducts,
                                          returnedTrialProducts: summary.returnedTrialProducts,
                                          returnedAssets: summary.returnedAssets,
                                          returnedProducts: summary.returnedProducts,
                                          deliveredProducts:
                                              summary.productsCount -
                                              summary.returnedProducts -
                                              summary.returnedAssets
                                      }) + " "
                              )
                            : t(`deliveryInfo.summary.deliveryCompleted`, {
                                  ns: "orderDetail"
                              })
                    }
                />
            </Steps>
        </Container>
    );
};

export default OrderSteps;
